import Avatar from '@mui/material/Avatar';
import LogRocket from 'logrocket';
import { ILoginReducer } from 'types/types';
import { Link } from 'react-router-dom';
import { Menu, MenuItem } from '@attain-sre/attain-design-system';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router';

import kdsLogo from '../../../public/logo1.png';
import { Badge, createStyles, makeStyles } from '@material-ui/core';

import {
  addNotification as addAudienceNotification,
  setAudienceLoadingState,
} from 'pages/audienceManager/slice';

import Pusher from 'pusher-js';
import {
  LoadingStates,
  PUSHER_NOTIFICATION_STATUS,
  UploadStates,
} from 'constants/index';
import { Notifications as NotificationsIcon } from '@material-ui/icons';
import {
  PUSHER_EMAIL_VALIDATION_MESSAGE,
  PUSHER_ENV_MISSING_MESSAGE,
} from '../../content';
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';
import { logOutAction } from 'pages/auth/actions';
import { updateAudienceSizeAndStatus } from 'pages/customAudiences/slice';
import { useUser } from 'reactfire';
import * as Styled from './GlobalHeader.styles';

const useStyles = makeStyles(() =>
  createStyles({
    kdsName: {
      color: 'white',
      fontSize: 24,
      fontWeight: 'bold',
      fontFamily: 'Calibre-Semibold, sans-serif',
    },
    link: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: 5,
    },
    notificationBadge: {
      padding: '8px',
      marginLeft: '-6px',
      color: '#DADEE2 !important',
      '&:hover': {
        color: 'white',
      },
      '&:focus': {
        color: 'white',
      },
    },
    notification: {
      cursor: 'pointer',
    },
  })
);

const GlobalHeader = () => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const history = useHistory();
  const { data: user } = useUser();
  const currentUser = user || { email: '' };

  const encodeBase64 = (str: string): string => {
    return btoa(str);
  };

  // Create user-specific channel name from email
  const createUserChannel = (email: string): string => {
    if (!email) {
      throw new Error(PUSHER_EMAIL_VALIDATION_MESSAGE);
    }
    return `ads-user-${encodeBase64(email)}`;
  };

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const authData: ILoginReducer = useSelector(
    (state: RootStateOrAny) => state.loginReducer
  );

  useEffect(() => {
    if (user?.uid && currentUser.email) {
      LogRocket.identify(user.uid, {
        email: currentUser.email,
      });
    }
  }, [user, currentUser.email]);

  // Handle notifications from Pusher
  const handlePusherNotification = (data: any) => {
    if (data.action === process.env.PUSHER_ACTION_CUSTOM_AUDIENCE_IMPORTER) {
      if (data?.status === PUSHER_NOTIFICATION_STATUS.DONE) {
        dispatch(setAudienceLoadingState({ state: LoadingStates.DONE }));
        dispatch(
          updateAudienceSizeAndStatus({
            size: data.totalCount || 0,
            status: UploadStates.DONE,
          })
        );
        dispatch(
          addAudienceNotification({
            state: LoadingStates.DONE,
            message: data?.message,
          })
        );
      }
    }
  };

  useEffect(() => {
    if (!currentUser.email) return;

    // Ensure environment variables are defined before using them
    const pusherApiKey = process.env.PUSHER_API_KEY;
    const pusherActions = [process.env.PUSHER_ACTION_CUSTOM_AUDIENCE_IMPORTER];

    if (!pusherApiKey || !pusherActions.length) {
      console.error(PUSHER_ENV_MISSING_MESSAGE);
      return;
    }

    // Initialize Pusher with the correct configuration
    const pusher = new Pusher(pusherApiKey, {
      cluster: 'us2',
      forceTLS: true,
    });

    // Create user-specific channel using email
    const userSpecificChannel = createUserChannel(currentUser.email || '');

    // Subscribe to the user-specific channel
    const channel = pusher.subscribe(userSpecificChannel);

    pusherActions.forEach((action) => {
      if (action) {
        channel.bind(action, handlePusherNotification);
      }
    });

    // Cleanup function
    return () => {
      pusherActions.forEach((action) => {
        if (action) {
          channel.unbind(action);
        }
      });
      pusher.unsubscribe(userSpecificChannel);
      pusher.disconnect();
    };
  }, [currentUser?.email, dispatch]);

  const handleClick = (e: any) => {
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const logOut = () => dispatch(logOutAction());

  const handleSignOut = () => {
    if (authData.data.res.uid) {
      logOut();
    }
  };

  const open = Boolean(anchorEl);
  const avatarText = currentUser.email?.slice(0, 2).toUpperCase();
  const id = open ? 'simple-Menu' : undefined;
  return (
    <Styled.Wrapper>
      <Link to="/" className={classes.link}>
        <img src={kdsLogo} alt="kds-logo" width={28} height={28} />
        <span className={classes.kdsName}>Attain Data Suite</span>
      </Link>

      <Styled.Actions>
        <Badge
          overlap="circular"
          className={classes.notificationBadge}
          style={{ display: 'none' }}
        >
          <NotificationsIcon
            className={classes.notification}
            onClick={() => {
              history.push('/main/notifications');
            }}
          />
        </Badge>

        <span
          role="button"
          tabIndex={0}
          aria-describedby={id}
          onClick={(e) => handleClick(e)}
        >
          <Avatar
            style={{
              backgroundColor: '#B8F4F1',
              color: '#1A171C',
              fontSize: 18,
              fontFamily: 'Calibre-Semibold, sans-serif',
              fontWeight: 600,
              lineHeight: 28,
              display: 'flex',
              height: 36,
              justifyContent: 'center',
              width: 36,
            }}
          >
            {avatarText}
          </Avatar>
        </span>

        <Menu
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem onClick={() => handleSignOut()}>Logout</MenuItem>
        </Menu>
      </Styled.Actions>
    </Styled.Wrapper>
  );
};

export default GlobalHeader;
